import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Button, cn, ErrorBox, useDebounce, useFetch, Popover, PopoverContent, PopoverTrigger, Command, CommandEmpty, CommandGroup, CommandItem, CommandInput } from "@vertesia/ui/core";
import { useUserSession } from "@vertesia/ui/session";
import { useUITranslation } from '../../../i18n/index.js';
export function SelectCollection({ onChange, value, disabled = false, placeholder, searchPlaceholder, filterOut, allowDynamic = true, multiple = false }) {
    const { client } = useUserSession();
    const { t } = useUITranslation();
    const resolvedPlaceholder = placeholder ?? t('store.selectACollection');
    const resolvedSearchPlaceholder = searchPlaceholder ?? t('store.searchCollections');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [useServerSearch, setUseServerSearch] = useState(false);
    // Debounce the search query to avoid excessive API calls (only used for server-side search)
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    // Memoize the search function to prevent unnecessary re-renders
    const searchCollections = useCallback(async (query) => {
        setIsSearching(true);
        const trimmedQuery = query.trim();
        const collections = await client.store.collections.search({
            dynamic: allowDynamic ? undefined : false,
            name: useServerSearch ? (trimmedQuery || undefined) : undefined
        });
        setIsSearching(false);
        // Check if we hit the maximum limit (1000 collections) - if so, enable server-side search
        if (!useServerSearch && collections.length >= 1000) {
            setUseServerSearch(true);
        }
        // Filter out collections if filterOut is provided
        if (filterOut && filterOut.length > 0) {
            return collections.filter(col => !filterOut.includes(col.id));
        }
        return collections;
    }, [client, allowDynamic, filterOut, useServerSearch]);
    // Fetch collections based on search mode
    const { data: collections, error } = useFetch(() => searchCollections(useServerSearch ? debouncedSearchQuery : ''), [useServerSearch ? debouncedSearchQuery : '', searchCollections]);
    // Memoize the selected collection(s)
    const selectedCollection = useMemo(() => {
        if (!collections)
            return multiple ? [] : undefined;
        if (multiple && Array.isArray(value)) {
            return collections.filter((collection) => value.includes(collection.id));
        }
        else if (!multiple && typeof value === 'string') {
            return collections.find((collection) => collection.id === value);
        }
        return multiple ? [] : undefined;
    }, [collections, value, multiple]);
    // Handle collection selection
    const handleSelect = useCallback((collection) => {
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : [];
            const isSelected = currentValues.includes(collection.id);
            if (isSelected) {
                // Remove from selection
                const newValues = currentValues.filter(id => id !== collection.id);
                const newCollections = collections?.filter(c => newValues.includes(c.id)) || [];
                onChange(newValues, newCollections);
            }
            else {
                // Add to selection
                const newValues = [...currentValues, collection.id];
                const newCollections = collections?.filter(c => newValues.includes(c.id)) || [];
                onChange(newValues, newCollections);
            }
        }
        else {
            onChange(collection.id, collection);
        }
    }, [onChange, value, collections, multiple]);
    // Handle clear selection
    const handleClear = useCallback(() => {
        onChange(undefined, undefined);
    }, [onChange]);
    // Handle search input change
    const handleSearchChange = useCallback((query) => {
        setSearchQuery(query);
    }, []);
    const hasSearchQuery = searchQuery.trim().length > 0;
    // Client-side filtering when not using server search
    const filteredCollections = useMemo(() => {
        if (!collections)
            return [];
        // If using server search, collections are already filtered by the server
        if (useServerSearch)
            return collections;
        // Otherwise, do client-side filtering
        if (!hasSearchQuery)
            return collections;
        const queryLower = searchQuery.toLowerCase();
        return collections.filter(col => col.name.toLowerCase().includes(queryLower));
    }, [collections, useServerSearch, hasSearchQuery, searchQuery]);
    const showClearOption = multiple
        ? Array.isArray(selectedCollection) && selectedCollection.length > 0
        : !!selectedCollection;
    const renderTrailingIcon = () => {
        if (showClearOption) {
            return (_jsx("span", { role: "button", "aria-label": "Clear selection", className: "ml-2 shrink-0 opacity-50 hover:opacity-100 hover:text-destructive cursor-pointer", onClick: (e) => {
                    e.stopPropagation();
                    handleClear();
                }, children: _jsx(X, { className: "h-4 w-4" }) }));
        }
        return _jsx(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" });
    };
    // Show error state
    if (error) {
        return (_jsx(ErrorBox, { title: t('store.collectionFetchFailed'), children: error.message }));
    }
    // Get display text for the button
    const getDisplayText = () => {
        if (multiple && Array.isArray(selectedCollection) && selectedCollection.length > 0) {
            if (selectedCollection.length === 1) {
                return selectedCollection[0].name;
            }
            return t('store.collectionsSelected', { count: selectedCollection.length });
        }
        else if (!multiple && selectedCollection && !Array.isArray(selectedCollection)) {
            return selectedCollection.name;
        }
        return resolvedPlaceholder;
    };
    return (_jsxs(Popover, { children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { variant: "outline", role: "combobox", "aria-haspopup": "listbox", className: cn("w-full justify-between min-w-0"), disabled: disabled, children: [_jsx("span", { className: "truncate flex-1 text-left min-w-0", children: getDisplayText() }), renderTrailingIcon()] }) }), _jsx(PopoverContent, { className: "mt-2 mb-2 w-[var(--radix-popover-trigger-width)] p-0", align: "start", children: _jsxs(Command, { shouldFilter: false, children: [_jsxs("div", { className: "flex justify-between items-center border-b px-3", "cmdk-input-wrapper": "", children: [_jsx(CommandInput, { placeholder: resolvedSearchPlaceholder, value: searchQuery, onValueChange: handleSearchChange, className: "flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50" }), isSearching && (_jsx("div", { className: "mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" }))] }), _jsx(CommandEmpty, { children: isSearching
                                ? t('store.searching')
                                : hasSearchQuery
                                    ? t('store.noCollectionsFound')
                                    : t('store.noCollectionsAvailable') }), _jsxs(CommandGroup, { className: "max-h-[300px] overflow-auto", children: [showClearOption && !hasSearchQuery && (_jsx(CommandItem, { value: "__clear__", onSelect: handleClear, className: "text-destructive", children: "Remove collection selection(s)" })), hasSearchQuery && (_jsx(CommandItem, { value: "__clear_search__", onSelect: () => setSearchQuery(""), className: "text-muted", children: t('store.clearSelection') })), filteredCollections.map((collection) => {
                                    const isSelected = multiple && Array.isArray(value)
                                        ? value.includes(collection.id)
                                        : value === collection.id;
                                    return (_jsxs(CommandItem, { value: collection.id, onSelect: () => handleSelect(collection), className: "flex items-center justify-between", children: [_jsx("span", { className: "truncate", children: collection.name }), isSelected && (_jsx(Check, { className: "ml-2 h-4 w-4 shrink-0" }))] }, collection.id));
                                })] })] }) })] }));
}
//# sourceMappingURL=SelectCollection.js.map